import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import LiveIndicator from "../../utils/LiveIndicator";
import { navItems, navItemsMobile } from "./HeaderData";
import "./SportsNav.css";
import {
  selectTopBaarBgColor,
  SelectUserLockApplications,
} from "../../utils/helper/commonSelectors";
import { launcherUrl } from "../../api";
import { isMobile } from "react-device-detect";

const filterInplayMatches = (matches) => {
  return matches?.data?.filter((e) =>
    e?.matchOdds?.some((odds) => odds?.inplay)
  );
};

const Navbar = () => {
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);

  const TopBaarBgColor = useSelector(selectTopBaarBgColor());
  const userData = useSelector((state) => state?.user?.userData);
  const lockData = useSelector(SelectUserLockApplications());

  const sportLockStatus = {
    cricket: lockData?.cricket,
    soccer: lockData?.soccer,
    tennis: lockData?.tennis,
    kabaddi: lockData?.kabaddi,
    horse: lockData?.horse,
    greyhound: lockData?.greyhound,
  };

  const filternavItemsMobile = navItemsMobile.filter((e) => {
    return !sportLockStatus[e?.name?.toLowerCase()];
  });
  const filternavItems = navItems.filter((e) => {
    return !sportLockStatus[e?.name?.toLowerCase()];
  });

  const navigate = useNavigate();

  const { matches: cricketMatches } = useSelector(
    (state) => state.matches.cricket
  );
  const { matches: soccerMatches } = useSelector(
    (state) => state.matches.soccer
  );
  const { matches: tennisMatches } = useSelector(
    (state) => state.matches.tennis
  );
  const horseRaceList = useSelector((state) => state.horse?.horseRaceList);
  const greyhoundRaceList = useSelector(
    (state) => state.greyhound?.greyhoundRaceList
  );

  const filteredCricketMatches = useMemo(
    () => filterInplayMatches(cricketMatches),
    [cricketMatches]
  );
  const filteredSoccerMatches = useMemo(
    () => filterInplayMatches(soccerMatches),
    [soccerMatches]
  );
  const filteredTennisMatches = useMemo(
    () => filterInplayMatches(tennisMatches),
    [tennisMatches]
  );

  const [liveInplayHorseMatches, setLiveInplayHorseMatches] = useState(0);
  const [liveInplayGreyhoundMatches, setLiveInplayGreyhoundMatches] =
    useState(0);

  const updateHorseLiveMatches = () => {
    let inplayCount = 0;
    horseRaceList?.forEach(({ children: country }) => {
      country?.forEach(({ children: matches }) => {
        matches?.forEach((match) => {
          const matchStartTime = new Date(match.stime);
          const matchLiveThreshold = new Date(
            matchStartTime.getTime() - 30 * 60 * 1000
          );
          const matchEndTime = new Date(
            matchStartTime.getTime() + 5 * 60 * 1000
          );
          const now = new Date();
          const isMatchLive = now >= matchLiveThreshold && now <= matchEndTime;
          if (isMatchLive) inplayCount += 1;
        });
      });
    });
    setLiveInplayHorseMatches(inplayCount);
  };

  const updateGreyhoundLiveMatches = () => {
    let inplayCount = 0;
    greyhoundRaceList?.forEach(({ children: country }) => {
      country?.forEach(({ children: matches }) => {
        matches?.forEach((match) => {
          const matchStartTime = new Date(match.stime);
          const matchLiveThreshold = new Date(
            matchStartTime.getTime() - 30 * 60 * 1000
          );
          const matchEndTime = new Date(
            matchStartTime.getTime() + 5 * 60 * 1000
          );
          const now = new Date();
          const isMatchLive = now >= matchLiveThreshold && now <= matchEndTime;
          if (isMatchLive) inplayCount += 1;
        });
      });
    });
    setLiveInplayGreyhoundMatches(inplayCount);
  };

  useEffect(() => {
    updateHorseLiveMatches();
    const horseIntervalId = setInterval(updateHorseLiveMatches, 60 * 1000);
    return () => clearInterval(horseIntervalId);
  }, [horseRaceList]);

  useEffect(() => {
    updateGreyhoundLiveMatches();
    const greyhoundIntervalId = setInterval(
      updateGreyhoundLiveMatches,
      60 * 1000
    );
    return () => clearInterval(greyhoundIntervalId);
  }, [greyhoundRaceList]);

  const getLiveCount = (name) => {
    switch (name) {
      case "In-Play":
        return (
          filteredCricketMatches?.length +
          filteredSoccerMatches?.length +
          filteredTennisMatches?.length +
          (liveInplayHorseMatches || 0) +
          (liveInplayGreyhoundMatches || 0)
        );
      case "Cricket":
        return filteredCricketMatches?.length;
      case "Soccer":
        return filteredSoccerMatches?.length;
      case "Tennis":
        return filteredTennisMatches?.length;
      case "Horse":
        return liveInplayHorseMatches;
      case "Greyhound":
        return liveInplayGreyhoundMatches;
      // case "Live Casino":
      //   return 20;
      case "Spribe":
        return 9;
      default:
        return 0;
    }
  };

  const handleAviatorClick = async (e) => {
    e.preventDefault();

    const data = {
      gameId: "SPB-aviator",
      playerId: userData?._id || "",
      displayName: userData?.casinoDisplayName || "",
      currency: "INR",
      country: "IN",
      gender: "M",
      birthDate: "1996-10-12",
      mode: isLoggedIn ? "real" : "demo",
      device: isMobile ? "mobile" : "desktop",
      returnUrl: window.location.href,
      walletSessionId: userData?.sessionToken || "",
    };

    try {
      const res = await launcherUrl(data);

      if (res?.success && res?.data?.url) {
        const gameUrl = encodeURIComponent(res.data.url);
        navigate(`/game-lobby?gameId=${gameUrl}`);
      } else {
        console.error("Launcher URL request failed", res);
      }
    } catch (error) {
      console.error(
        "Error launching Aviator:",
        error?.response?.data?.message || error.message
      );
    }
  };

  const DesktopNavItem = ({ item }) => (
    <NavLink
      to={item.href}
      onClick={item.name === "Aviator" ? handleAviatorClick : undefined}
      style={{ height: "40px", paddingTop: 10 }}
      className={({ isActive }) =>
        `nav-link font-bold text-[13px] ${isActive ? "active" : ""} ${item.isDisabled ? "cursor-pointer" : ""
        }`
      }
    >
      <span
        key={item.name}
        className={`${item?.name} relative whitespace-nowrap`}
      >
        {item.livestatus && item?.name !== "Aviator" && (
          <span className="absolute top-[-18px] right-[-20px] z-10 pointer-events-none">
            <LiveIndicator count={getLiveCount(item?.name)} />
          </span>
        )}
        {item.name}
      </span>
    </NavLink>
  );

  const MobileNavItem = ({ item }) => {
    const linkRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
      if (linkRef.current && location.pathname === item.href) {
        linkRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }, [location.pathname, item.href]);

    return (
      <NavLink
        to={item.href}
        ref={linkRef}
        onClick={item.name === "Aviator" ? handleAviatorClick : undefined}
        className={({ isActive }) =>
          `nav-link font-bold h-[54px] text-[13px] relative transition-all duration-300 ease-in-out ${isActive
            ? `border-b-[3px] border-[#1dc3c9]`
            : "border-b-[3px] border-transparent"
          } ${item.isDisabled ? "cursor-pointer" : ""}`
        }
      >
        <span
          key={item.name}
          className={`${item.name} relative whitespace-nowrap flex flex-col items-center justify-center pl-1 pr-2 py-1`}
        >
          {item.livestatus && item.name !== "Aviator" && (
            <span className="absolute top-[-3px] right-[-12px] z-10 pointer-events-none">
              <LiveIndicator count={getLiveCount(item?.name)} />
            </span>
          )}

          {item.icon && (
            <div
              className="w-[28px] h-[24px] bg-[url('/sports-icons.png')] bg-no-repeat"
              style={{
                backgroundPosition: `0px ${item.left}`,
                backgroundSize: "28px auto",
              }}
            />
          )}

          {/* {item?.name === "Aviator" && (
            <span className="z-10">
              <img src="/Icon/aviator.svg" alt="" width={40} height={40} />
            </span>
          )} */}

          {item?.name === "Kabaddi" && (
            <img src="/kabaddi.png" alt="kabaddi" width={28} height={24} />
          )}

          {item?.name === "Spribe" && (
            // <img
            //   src="/Icon/spribe.svg"
            //   alt="Spribe"
            //   width={32}
            //   height={32}
            //   className="mt-3"
            // />
            <span className="z-5 ml-[-15px] pt-[5px] transform rotate-15">
              <img src="/Icon/aviator.svg" alt="" width={32} height={32} />
            </span>
          )}

          {item?.name === "Vivo" && (
            <img
              src="/Icon/vivo.gif"
              alt="Spribe"
              width={30}
              height={30}
              className=""
            />
          )}

          <span className="text-xs font-bold mt-[2px]">{item.name}</span>
        </span>
      </NavLink>
    );
  };

  return (
    <section>
      {/* Desktop Navigation */}
      <nav
        className="text-white hidden nav-change:block"
        style={{
          background: `linear-gradient(180deg, ${TopBaarBgColor?.start}, ${TopBaarBgColor?.start})`,
        }}
      >
        <div className="flex text-sm hide-scrollbar">
          {filternavItems.map((item) => (
            <DesktopNavItem key={item.name} item={item} />
          ))}
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav
        className="bg-secondary asian-sports-nav text-white block overflow-x-scroll nav-change:hidden"
        style={{
          background: `linear-gradient(180deg, ${TopBaarBgColor?.start}, ${TopBaarBgColor?.start})`,
        }}
      >
        <div className="flex text-sm">
          {filternavItemsMobile.map((item) => (
            <MobileNavItem key={item.name} item={item} />
          ))}
        </div>
      </nav>
    </section>
  );
};

export default React.memo(Navbar);
